import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Chip,
  Alert,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AttachIcon from "@mui/icons-material/AttachFile";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SyncIcon from "@mui/icons-material/Sync";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Component } from "../../../../types/component";
import { Airframe } from "../../../../types/airframe";
import { SubAssembly } from "../../../../types/subAssembly";
import { AttachedComponents } from "./AttachedComponents";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { MINI_PACK_ICON_TOOLTIP } from "../../../../utils/miniPackExportJobUtils";
import type { DisplayPreferences } from "../../../../hooks/useTenantDisplayPreferences";
import { OwnershipDisplayLines } from "../../../common/OwnershipDisplayLines";

type Props = {
  pkg: Airframe;
  attached: (tailNumber: string) => Component[];
  subAssemblies: SubAssembly[];
  getInstalledComponentsForAirframe: (airframe: Airframe) => Component[];
  getComponentDisplayName: (componentId: string) => string;
  onAttach: (pkg: Airframe) => void;
  onEdit: (pkg: Airframe) => void;
  onDelete: (pkg: Airframe) => void;
  onMiniPack: (pkg: Airframe) => void;
  onReplace: (pkg: Airframe, component: Component) => void;
  onDetach: (componentId: string,airframeId?: string,slotId?: string) => void;
  displayPreferences: DisplayPreferences;
};

export function AirframeCard({
  pkg,
  subAssemblies,
  getInstalledComponentsForAirframe,
  onAttach,
  onEdit,
  onDelete,
  onMiniPack,
  onReplace,
  onDetach,
  displayPreferences,
}: Readonly<Props>) {
  const canUpdateAirframe = useHasPermission('assets-airframes', 'update');
  const canDeleteAirframe = useHasPermission('assets-airframes', 'delete');
  const canAttachComponent = useHasPermission('assets-airframes', 'create');
  const canDetachComponent = useHasPermission('assets-airframes', 'delete');
  const canAccessMiniPackExport = useHasPermission('assets-mini-pack-export', 'read');

  if (!pkg.tailNumber) {
    return null;
  }

  // Get installed components from slots instead of legacy attached method
  const attachedComponents = getInstalledComponentsForAirframe(pkg);

  const isFolderPending = pkg.folderStatus === "Pending";
  const isFolderFailed = pkg.folderStatus === "Failed";

  return (
    <Accordion
      sx={{
        mb: 1,
        bgcolor: "#222",
        color: "#fff",
        border: "1px solid #333",
        "&:before": { display: "none" },
        "&.Mui-expanded": { margin: "0 0 8px 0" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}>
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <Typography sx={{ color: "hsl(19 101% 51% / 1)", mr: 2 }}>
            {pkg.tailNumber}
          </Typography>
          {isFolderPending && (
            <Chip
              label="Syncing folders"
              size="small"
              icon={<SyncIcon sx={{ fontSize: 14, animation: "spin 2s linear infinite", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />}
              sx={{ bgcolor: "#1e3a5f", color: "#90caf9", mr: 1 }}
            />
          )}
          {isFolderFailed && (
            <Chip
              label="Folder error"
              size="small"
              icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
              sx={{ bgcolor: "#5f3a1e", color: "#ffb74d", mr: 1 }}
            />
          )}
          <Chip
            label={`${attachedComponents.length} assemblies`}
            size="small"
            sx={{
              bgcolor: "hsl(19 101% 51% / 0.2)",
              color: "hsl(19 101% 51% / 1)",
            }}
          />
        </Box>

        <Box>
          {canAttachComponent && (
            <IconButton
              size="small"
              sx={{ color: "hsl(19 101% 51% / 1)", mr: 1 }}
              onClick={(e) => {
                e.stopPropagation();
                onAttach(pkg);
              }}
            >
              <AttachIcon />
            </IconButton>
          )}

          {canUpdateAirframe && canAccessMiniPackExport && (
            <Tooltip title={MINI_PACK_ICON_TOOLTIP}>
              <IconButton
                size="small"
                sx={{ color: "hsl(19 101% 51% / 1)", mr: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMiniPack(pkg);
                }}
                aria-label="Configure mini-pack PDF"
              >
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
          )}

          {canUpdateAirframe && (
            <IconButton
              size="small"
              sx={{ color: "hsl(19 101% 51% / 1)", mr: 1 }}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(pkg);
              }}
            >
              <EditIcon />
            </IconButton>
          )}

          {canDeleteAirframe && (
            <IconButton
              size="small"
              sx={{ color: "#f44336" }}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(pkg);
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </AccordionSummary>

      {isFolderPending && (
        <Alert
          severity="info"
          icon={<SyncIcon sx={{ animation: "spin 2s linear infinite", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />}
          sx={{ mx: 2, mt: 1 }}
        >
          Setting up document folders .This airframe was created via bulk upload. Folders will be ready shortly.
        </Alert>
      )}

      {isFolderFailed && (
        <Alert
          severity="warning"
          icon={<ErrorOutlineIcon />}
          sx={{ mx: 2, mt: 1 }}
        >
          Document folder setup failed. The system will retry automatically.
        </Alert>
      )}

      <AccordionDetails sx={{ color: "#ccc", bgcolor: "#1a1a1a" }}>
        <Section title="Basic Information">
          <Item label="Manufacturer" value={pkg.manufacturer} />
          <Item label="Model" value={pkg.model} />
          <Item label="Tail Number" value={pkg.tailNumber} />
          <Item label="Serial Number" value={pkg.serialNumber} />
          <Item label="Date Manufactured" value={pkg.dateManufactured} />
        </Section>

        <Section title="Flight Details">
          <Item label="Total Flight Hours" value={pkg.totalFlightHours} />
          <Item label="Total Flight Cycles" value={pkg.totalFlightCycles} />
        </Section>

        <Section title="Heavy Maintenance Visit (HMV)">
          <Item label="Last HMV Date" value={pkg.lastHMVDate ? pkg.lastHMVDate : undefined} />
          <Item label="Last HMV Type" value={pkg.lastHMVType ? pkg.lastHMVType : undefined} />
        </Section>

        <Section title="Categories">
          <Item label="Operational Category" value={pkg.operationalCategory} />
          <Item label="Landing Category" value={pkg.landingCategory} />
          <Item label="ETOPS Category" value={pkg.etopsCategory} />
        </Section>

        <OwnershipDisplayLines
          preferences={displayPreferences}
          ownershipStatus={pkg.ownershipStatus}
          ownershipStatusLabel={pkg.ownershipStatusLabel}
          operatorName={pkg.operatorName}
          showOperator
          sectionTitle="Ownership"
        />

        <Section title="Attached Assemblies">
          <AttachedComponents
            components={attachedComponents}
            subAssemblies={subAssemblies}
            onReplace={(c) => onReplace(pkg, c)}
            onDetach={onDetach}
            canUpdate={canUpdateAirframe}
            canDetach={canDetachComponent}
          />
        </Section>

        {pkg.customFields && (
          <Typography sx={{ color: "#ccc", mt: 2 }}>
            <Box component="span" sx={{ color: "hsl(19 101% 51% / 1)", fontWeight: 600 }}>
              Additional Notes:{" "}
            </Box>
            {pkg.customFields}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}


function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <Typography
        sx={{ color: "hsl(19 101% 51% / 1)", mt: 2, mb: 1 }}
        variant="subtitle1"
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1,
          fontSize: "0.9rem",
        }}
      >
        {children}
      </Box>
    </>
  );
}

function Item({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) {
  // Don't render if value is empty, null, undefined, or zero-length string
  if (!value && value !== 0) {
    return null;
  }
  
  return (
    <Box>
      <strong>{label}:</strong> {value}
    </Box>
  );
}
